import { useState } from "react";

import { useArtifacts, useCurrentProjectId, useSources } from "@/api/queries";
import { ArtifactDetail } from "@/components/ArtifactDetail";
import { Button } from "@/components/Button";
import { DefinitionList } from "@/components/DefinitionList";
import { EmptyState } from "@/components/EmptyState";
import { Field, TextArea } from "@/components/Field";
import { Panel, SectionTitle } from "@/components/Panel";
import { RemoteState } from "@/components/RemoteState";
import { SegmentControl } from "@/components/SegmentControl";
import { SourceCreateModal } from "@/components/SourceCreateModal";
import { StatusLabel } from "@/components/StatusLabel";
import { Tag } from "@/components/Tag";

/* Canon / State 的 content 形态（与后端 JSON Schema 一致）。 */

interface CanonPlace {
  id: string;
  name: string;
  access: string;
  restricted?: boolean;
}

interface CanonInstitution {
  id: string;
  name: string;
  effect: string;
}

interface CanonCharacter {
  id: string;
  name: string;
  role: string;
  truth: string;
  bottom_lines: string[];
}

interface CanonGapItem {
  id: string;
  topic: string;
  blocks: string[];
  status: string;
}

interface CanonContent {
  world?: {
    era?: string;
    places?: CanonPlace[];
    institutions?: CanonInstitution[];
  };
  status?: string;
  canon_version?: string;
  characters?: CanonCharacter[];
  locked_facts?: string[];
  canon_gaps?: CanonGapItem[];
}

interface StateItem {
  id: string;
  label: string;
  type: string;
  scope: string;
  visibility: string;
  value_shape: string;
  authority: string;
  reversible?: boolean;
  observable_feedback: string;
  tiers?: string[];
}

/** 关系单向侧（`relationship.schema.json` 的 `a_to_b` / `b_to_a`）。 */
interface RelationshipSide {
  trust?: number | null;
  intimacy?: number | null;
  respect?: number | null;
  affect_tags?: string[];
  preconceptions?: string[];
}

/** 关系对象（`relationship.schema.json`）。 */
interface CanonRelationship {
  id: string;
  pair: string[];
  formation_state: string;
  objective_contract: string;
  contract_tags?: string[];
  objective_power_balance?: number | null;
  a_to_b?: RelationshipSide;
  b_to_a?: RelationshipSide;
  knowledge_flags?: { a?: string[]; b?: string[]; mutual?: string[] };
  debts?: unknown[];
  promises?: unknown[];
  exit_rights?: { a?: string; b?: string };
}

interface StateContent {
  states?: StateItem[];
  dictionary_version?: string;
  relationships?: CanonRelationship[];
}

type CanonTab = "world" | "characters" | "relations" | "facts" | "states" | "gaps";

const TABS: { value: CanonTab; label: string }[] = [
  { value: "world", label: "世界规则" },
  { value: "characters", label: "人物" },
  { value: "relations", label: "人物关系" },
  { value: "facts", label: "锁定事实" },
  { value: "states", label: "状态字典" },
  { value: "gaps", label: "Canon 缺口" },
];

/**
 * 真相层（S2 / G2）。
 *
 * 展示的是**不可协商的事实**：世界规则、人物底线、锁定事实、状态字典与待研究缺口。
 * 数据来自当前项目已批准的 `canon` / `state` Artifact，未通过研究关口的内容不得写入正式版本。
 *
 * @dataSource remote
 */
export function CanonView() {
  const projectId = useCurrentProjectId();
  const sources = useSources(projectId);
  const canonList = useArtifacts(projectId, "canon");
  const stateList = useArtifacts(projectId, "state");
  const [tab, setTab] = useState<CanonTab>("world");
  const [gapDraft, setGapDraft] = useState("");
  const [detailId, setDetailId] = useState<string | null>(null);
  const [sourceOpen, setSourceOpen] = useState(false);

  const canonArtifact = canonList.data?.[0] ?? null;
  const stateArtifact = stateList.data?.[0] ?? null;
  const canon = (canonArtifact?.content ?? {}) as unknown as CanonContent;
  const states = (stateArtifact?.content ?? {}) as unknown as StateContent;

  const places = canon.world?.places ?? [];
  const institutions = canon.world?.institutions ?? [];
  const characters = canon.characters ?? [];
  const lockedFacts = canon.locked_facts ?? [];
  const gaps = canon.canon_gaps ?? [];
  const stateList2 = states.states ?? [];
  const relationships = states.relationships ?? [];

  return (
    <div className="page-scroll">
      <div className="page-width">
        <RemoteState queries={[canonList, stateList, sources]} title="真相层加载失败">
          {!canonArtifact ? (
            <EmptyState
              title="本项目还没有 Canon 产物"
              description="Canon 由真相层能力生成并通过 G2 关口批准后才可用。"
            />
          ) : (
            <>
              <header className="page-heading">
                <div>
                  <span className="eyebrow">S2 · 不可协商的真相</span>
                  <h2>Canon 一旦锁定，后面所有的选择都要对它负责</h2>
                </div>
                <div className="meta-row">
                  <Tag tone="green">v{canon.canon_version ?? "—"}</Tag>
                  <Tag tone={canonArtifact.status === "approved" ? "blue" : "gold"}>
                    {canonArtifact.status === "approved" ? "G2 已锁定" : "未锁定"}
                  </Tag>
                  <Tag>{canonArtifact.title}</Tag>
                </div>
              </header>

              <section className="kpi-strip" aria-label="真相层关键数据">
                <div className="kpi">
                  <span>人物</span>
                  <strong>{characters.length}</strong>
                  <small>含底线声明</small>
                </div>
                <div className="kpi">
                  <span>地点</span>
                  <strong>{places.length}</strong>
                  <small>含访问约束</small>
                </div>
                <div className="kpi">
                  <span>状态定义</span>
                  <strong>{stateList2.length}</strong>
                  <small>v{states.dictionary_version ?? "—"}</small>
                </div>
                <div className="kpi">
                  <span>Canon 缺口</span>
                  <strong>{gaps.length}</strong>
                  <small>需研究后关闭</small>
                </div>
              </section>

              <SectionTitle
                title="真相层内容"
                description={canonArtifact.title}
                actions={
                  <SegmentControl<CanonTab>
                    label="真相层分区"
                    value={tab}
                    onChange={setTab}
                    options={TABS}
                  />
                }
              />

              {tab === "world" ? (
                <div className="canon-grid">
                  {places.map((place) => (
                    <article className="canon-card" key={place.id}>
                      <div className="meta-row">
                        <Tag>{place.id}</Tag>
                        {place.restricted ? (
                          <Tag tone="red">受限空间</Tag>
                        ) : (
                          <Tag tone="green">可自由进入</Tag>
                        )}
                      </div>
                      <h4 style={{ marginTop: 10 }}>{place.name}</h4>
                      <p>{place.access}</p>
                    </article>
                  ))}
                  {institutions.map((institution) => (
                    <article className="canon-card" key={institution.id}>
                      <div className="meta-row">
                        <Tag tone="violet">{institution.id}</Tag>
                      </div>
                      <h4 style={{ marginTop: 10 }}>{institution.name}</h4>
                      <p>{institution.effect}</p>
                    </article>
                  ))}
                </div>
              ) : null}

              {tab === "characters" ? (
                <div className="canon-grid">
                  {characters.map((character) => (
                    <article className="canon-card" key={character.id}>
                      <div className="meta-row">
                        <Tag>{character.role}</Tag>
                      </div>
                      <h4 style={{ marginTop: 10 }}>{character.name}</h4>
                      <p>{character.truth}</p>
                      <div className="section-title" style={{ marginTop: 12 }}>
                        <div>
                          <h3>底线</h3>
                          <p>违反即视为人物崩坏（R03）</p>
                        </div>
                      </div>
                      <ul className="issue-list">
                        {character.bottom_lines.map((line) => (
                          <li className="issue-item" key={line}>
                            <span className="mini-mark">!</span>
                            <div>
                              <p>{line}</p>
                            </div>
                          </li>
                        ))}
                      </ul>
                    </article>
                  ))}
                </div>
              ) : null}

              {tab === "relations" ? (
                <RelationshipPanel relationships={relationships} characters={characters} />
              ) : null}

              {tab === "facts" ? (
                <Panel
                  title="锁定事实"
                  description="任何产物不得与之冲突"
                  bodyClassName="idea-list"
                >
                  <ul className="issue-list">
                    {lockedFacts.map((fact) => (
                      <li className="issue-item" key={fact}>
                        <span className="mini-mark">✓</span>
                        <div>
                          <p>{fact}</p>
                        </div>
                      </li>
                    ))}
                  </ul>
                </Panel>
              ) : null}

              {tab === "states" ? (
                <div className="canon-grid">
                  {stateList2.map((state) => (
                    <article className="canon-card" key={state.id}>
                      <div className="meta-row">
                        <Tag tone="blue">{state.id}</Tag>
                        <Tag>{state.type}</Tag>
                        <Tag tone={state.visibility === "hidden" ? "red" : "green"}>
                          {state.visibility}
                        </Tag>
                      </div>
                      <h4 style={{ marginTop: 10 }}>{state.label}</h4>
                      <p>{state.observable_feedback}</p>
                      <DefinitionList
                        entries={[
                          {
                            term: "值形态",
                            value: `${state.value_shape}${
                              state.tiers ? `（${state.tiers.join(" / ")}）` : ""
                            }`,
                          },
                          { term: "作用域", value: state.scope },
                          {
                            term: "可逆性",
                            value:
                              state.reversible === true
                                ? "可逆"
                                : state.reversible === false
                                  ? "不可逆"
                                  : "条件可逆",
                          },
                          { term: "写入权限", value: state.authority },
                        ]}
                      />
                    </article>
                  ))}
                </div>
              ) : null}

              {tab === "gaps" ? (
                <div className="wizard-layout">
                  <Panel
                    title="待研究的 Canon 缺口"
                    description="关闭前，相关内容不得写入正式版本"
                    bodyClassName="idea-list"
                  >
                    {gaps.length === 0 ? (
                      <p className="inline-note">没有未关闭的缺口。</p>
                    ) : (
                      <ul className="issue-list">
                        {gaps.map((gap) => (
                          <li className="issue-item" key={gap.id}>
                            <span className="mini-mark">?</span>
                            <div>
                              <p>
                                {gap.id} · {gap.topic}
                              </p>
                              <small>阻塞：{gap.blocks.join(" / ") || "—"}</small>
                            </div>
                            <StatusLabel tone={gap.status === "open" ? "warning" : "success"}>
                              {gap.status === "open" ? "待研究" : "已关闭"}
                            </StatusLabel>
                          </li>
                        ))}
                      </ul>
                    )}
                    <div style={{ marginTop: 16 }}>
                      <Field
                        id="gap-draft"
                        label="补充一条缺口"
                        hint="提交后进入候选，不直接改变 Canon"
                      >
                        <TextArea
                          id="gap-draft"
                          rows={3}
                          value={gapDraft}
                          placeholder="例如：宋代寡妇再嫁需要哪些程序性文件？"
                          onChange={(event) => setGapDraft(event.target.value)}
                        />
                      </Field>
                      <div className="toolbar-right" style={{ marginTop: 10 }}>
                        <Button
                          size="small"
                          disabled={gapDraft.trim().length === 0}
                          onClick={() => setGapDraft("")}
                        >
                          提交候选
                        </Button>
                      </div>
                    </div>
                  </Panel>

                  <aside>
                    <Panel
                      title="来源与权限"
                      description="rights_status 未确认不得进入正式版本"
                      actions={
                        <Button
                          size="small"
                          variant="quiet"
                          id="register-source"
                          onClick={() => setSourceOpen(true)}
                        >
                          登记资料
                        </Button>
                      }
                    >
                      {(sources.data ?? []).length === 0 ? (
                        <p className="inline-note">尚未登记资料。</p>
                      ) : (
                        <ul className="activity-list">
                          {(sources.data ?? []).map((source) => (
                            <li className="activity-item" key={source.id}>
                              <span className="mini-mark">¶</span>
                              <div>
                                <p>{source.title}</p>
                                <small>
                                  {source.kind} · {source.rights_status}
                                </small>
                              </div>
                            </li>
                          ))}
                        </ul>
                      )}
                    </Panel>

                    <Panel
                      title="当前真相层产物"
                      description="approved 状态即为不可变对象（V102）"
                      className="community-funnel"
                    >
                      <ul className="activity-list">
                        {[canonArtifact, stateArtifact]
                          .filter((artifact) => artifact !== null)
                          .map((artifact) => (
                            <li className="activity-item" key={artifact.id}>
                              <span className="mini-mark">✓</span>
                              <div>
                                <p>{artifact.title}</p>
                                <small>
                                  {artifact.type} · v{artifact.version} · {artifact.status} ·{" "}
                                  {artifact.gate ?? "—"}
                                </small>
                              </div>
                              <div className="story-card-actions">
                                <Button
                                  size="small"
                                  variant="quiet"
                                  onClick={() => setDetailId(artifact.id)}
                                >
                                  详情
                                </Button>
                              </div>
                            </li>
                          ))}
                      </ul>
                    </Panel>
                  </aside>
                </div>
              ) : null}
            </>
          )}
        </RemoteState>
      </div>

      <ArtifactDetail artifactId={detailId} onClose={() => setDetailId(null)} />
      <SourceCreateModal open={sourceOpen} onClose={() => setSourceOpen(false)} />
    </div>
  );
}

/* ------------------------------------------------------------- 人物关系方向图（§12.4） */

const FORMATION_LABEL: Record<string, string> = {
  unformed: "未形成",
  indirect: "间接",
  minimal_contact: "最小接触",
  direct: "直接",
};

const CONTRACT_LABEL: Record<string, string> = {
  stranger: "陌生人",
  imposed: "被强加",
  transaction: "交易",
  alliance: "同盟",
  intimate: "亲密",
  separation: "分离",
  rupture: "破裂",
};

/**
 * 单向三轴渲染。
 *
 * `null` 与 `0` 在关系里语义不同（R06：`unformed` 的三轴必须保持 `null`），
 * 因此这里把 `null` / `undefined` 一律显示为「—」，**不洗成 `0`**。
 */
function sideValue(value: number | null | undefined): string {
  return value === null || value === undefined ? "—" : String(value);
}

function RelationshipSideCard({ label, side }: { label: string; side?: RelationshipSide }) {
  return (
    <article className="rel-side">
      <h5>{label}</h5>
      <DefinitionList
        entries={[
          { term: "信任", value: sideValue(side?.trust) },
          { term: "亲密", value: sideValue(side?.intimacy) },
          { term: "尊重", value: sideValue(side?.respect) },
          { term: "情感标签", value: (side?.affect_tags ?? []).join(" / ") || "—" },
          { term: "前置印象", value: (side?.preconceptions ?? []).join(" / ") || "—" },
        ]}
      />
    </article>
  );
}

/**
 * 人物关系方向图 + 明细。
 *
 * 数据来自当前项目的 `state` 产物 `content.relationships`（`relationship.schema.json`），
 * 不新增请求。图只呈现、不判定——关系的形成态与三轴合法性由 R06 在服务端判。
 */
function RelationshipPanel({
  relationships,
  characters,
}: {
  relationships: CanonRelationship[];
  characters: CanonCharacter[];
}) {
  const [selectedId, setSelectedId] = useState<string | null>(relationships[0]?.id ?? null);
  const selected = relationships.find((item) => item.id === selectedId) ?? relationships[0] ?? null;

  if (relationships.length === 0 || selected === null) {
    return (
      <EmptyState
        title="本项目还没有关系对象"
        description="关系由状态字典产物声明；未形成关系的三轴保持 null，不会显示为 0。"
      />
    );
  }

  const nameOf = (id: string) => characters.find((item) => item.id === id)?.name ?? id;
  const nodeIds = [...new Set(relationships.flatMap((item) => item.pair))];

  // 环形布局：位置由节点数决定，几何常量内联（沿用 StoryView 的做法）。
  const CX = 340;
  const CY = 200;
  const RADIUS = 140;
  const positions = new Map<string, { x: number; y: number }>();
  nodeIds.forEach((id, index) => {
    const angle = (2 * Math.PI * index) / nodeIds.length - Math.PI / 2;
    positions.set(id, { x: CX + RADIUS * Math.cos(angle), y: CY + RADIUS * Math.sin(angle) });
  });

  return (
    <div className="rel-layout">
      <div className="rel-map">
        <svg viewBox="0 0 680 400" role="img" aria-label="人物关系方向图">
          <defs>
            <marker id="rel-arrow" markerWidth="8" markerHeight="8" refX="6" refY="3" orient="auto">
              <path d="M0,0 L6,3 L0,6 Z" style={{ fill: "var(--ink-soft)" }} />
            </marker>
          </defs>
          {relationships.map((rel) => {
            const [a, b] = rel.pair;
            const pa = positions.get(a);
            const pb = positions.get(b);
            if (!pa || !pb) return null;
            const active = rel.id === selected.id;
            const balance = rel.objective_power_balance;
            const label = `${CONTRACT_LABEL[rel.objective_contract] ?? rel.objective_contract}${
              balance === null || balance === undefined
                ? ""
                : ` · ${balance > 0 ? "+" : ""}${balance}`
            }`;
            return (
              <g
                key={rel.id}
                className="rel-edge"
                role="button"
                tabIndex={0}
                aria-label={`查看关系 ${rel.id}`}
                aria-pressed={active}
                onClick={() => setSelectedId(rel.id)}
                onKeyDown={(event) => {
                  if (event.key === "Enter" || event.key === " ") {
                    event.preventDefault();
                    setSelectedId(rel.id);
                  }
                }}
              >
                <line
                  x1={pa.x}
                  y1={pa.y}
                  x2={pb.x}
                  y2={pb.y}
                  style={{
                    stroke: active ? "var(--violet)" : "var(--line-strong)",
                    strokeWidth: active ? 2 : 1,
                  }}
                  markerStart="url(#rel-arrow)"
                  markerEnd="url(#rel-arrow)"
                />
                <text
                  className="rel-edge-label"
                  x={(pa.x + pb.x) / 2}
                  y={(pa.y + pb.y) / 2 - 6}
                  textAnchor="middle"
                >
                  {label}
                </text>
              </g>
            );
          })}
          {nodeIds.map((id) => {
            const point = positions.get(id);
            if (!point) return null;
            const involved = selected.pair.includes(id);
            return (
              <g key={id}>
                <circle
                  cx={point.x}
                  cy={point.y}
                  r={26}
                  style={{
                    fill: involved ? "var(--violet-soft)" : "var(--paper-strong)",
                    stroke: involved ? "var(--violet)" : "var(--line)",
                  }}
                />
                <text className="rel-node-id" x={point.x} y={point.y - 2} textAnchor="middle">
                  {id.replace("CHAR_", "")}
                </text>
                <text className="rel-node-name" x={point.x} y={point.y + 44} textAnchor="middle">
                  {nameOf(id)}
                </text>
              </g>
            );
          })}
        </svg>
      </div>

      <aside className="rel-detail">
        <Panel
          title="关系明细"
          description="方向语义双向可辨：上行 a→b，下行 b→a"
          bodyClassName="idea-list"
        >
          <div className="meta-row">
            <Tag tone="violet">{selected.id}</Tag>
            <Tag>{FORMATION_LABEL[selected.formation_state] ?? selected.formation_state}</Tag>
            <Tag>{CONTRACT_LABEL[selected.objective_contract] ?? selected.objective_contract}</Tag>
            {selected.objective_power_balance === null ||
            selected.objective_power_balance === undefined ? null : (
              <Tag tone={selected.objective_power_balance < 0 ? "red" : "green"}>
                权力 {selected.objective_power_balance > 0 ? "+" : ""}
                {selected.objective_power_balance}
              </Tag>
            )}
          </div>

          <div className="rel-sides">
            <RelationshipSideCard
              label={`${nameOf(selected.pair[0])} → ${nameOf(selected.pair[1])}`}
              side={selected.a_to_b}
            />
            <RelationshipSideCard
              label={`${nameOf(selected.pair[1])} → ${nameOf(selected.pair[0])}`}
              side={selected.b_to_a}
            />
          </div>

          <DefinitionList
            entries={[
              { term: "契约标签", value: (selected.contract_tags ?? []).join(" / ") || "—" },
              {
                term: "共同知情",
                value: (selected.knowledge_flags?.mutual ?? []).join(" / ") || "—",
              },
              { term: "未偿债务", value: String((selected.debts ?? []).length) },
              {
                term: "退出权",
                value: `${selected.exit_rights?.a ?? "unknown"} / ${selected.exit_rights?.b ?? "unknown"}`,
              },
            ]}
          />
        </Panel>
      </aside>
    </div>
  );
}
